/**
 * FlowHub - Main Application Component
 */

import { useEffect, useRef } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { Spin, App as AntdApp, ConfigProvider } from 'antd';
import { useAuthStore } from './features/auth/store/authStore';
import { EnvValidator } from './shared/components/EnvValidator';
import { usePerformanceMonitoring } from './shared/hooks/usePerformanceMonitoring';
import { logger } from './shared/lib/logger';
import AppRouter from './app/router';

function App() {
  const { isLoading } = useAuthStore();
  const hasInitialized = useRef(false);

  // Monitor performance metrics in production
  usePerformanceMonitoring();

  // Check if environment is configured
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  
  const isEnvConfigured = supabaseUrl && 
                          supabaseKey && 
                          supabaseUrl !== 'your-project-url.supabase.co' &&
                          supabaseKey !== 'your-anon-key-here';

  // Load user session on mount - runs only once
  useEffect(() => {
    if (!isEnvConfigured || hasInitialized.current) return;
    hasInitialized.current = true;

    logger.debug('[App] Initializing user session');
    
    // Safety timeout: force isLoading=false after 6s to prevent infinite spinner
    const timeout = setTimeout(() => {
      const state = useAuthStore.getState();
      if (state.isLoading) {
        logger.warn('[App] loadUser timeout, forcing isLoading=false');
        useAuthStore.setState({ isLoading: false });
      }
    }, 6000);

    useAuthStore.getState().loadUser().finally(() => {
      clearTimeout(timeout);
      logger.debug('[App] User session loaded');
    });

    return () => clearTimeout(timeout);
  }, [isEnvConfigured]);

  // Show configuration guide if environment is not set up
  if (!isEnvConfigured) {
    return <EnvValidator />;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spin size="large" spinning tip="Loading FlowHub...">
          <div style={{ minHeight: 200 }} />
        </Spin>
      </div>
    );
  }

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: '#7c3aed',
          colorInfo: '#7c3aed',
          colorSuccess: '#10b981',
          colorWarning: '#f59e0b',
          colorError: '#ef4444',
          borderRadius: 12,
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial',
        },
        components: {
          Card: {
            borderRadiusLG: 16,
          },
          Button: {
            borderRadiusLG: 12,
          },
        },
      }}
    >
      <AntdApp>
        <BrowserRouter
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <AppRouter />
        </BrowserRouter>
      </AntdApp>
    </ConfigProvider>
  );
}

export default App;
